import { CheckCircle, Check } from "lucide-react";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/Button";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { getPlans } from "../services/storage";
import { useState, useEffect } from "react";

export function Planos() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const plans = getPlans();

  // Verifica se algum plano tem desconto anual ativado
  const hasYearlyDiscount = plans.some(plan => plan.yearlyDiscountEnabled);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(price);
  };

  const comparisonRows = [
    { feature: "Número de funcionários", basico: "Até 50", premium: "51 a 100", personalizado: "Acima de 100" },
    { feature: "Canal de Denúncias Completo", basico: true, premium: true, personalizado: true },
    { feature: "Canal Personalizável", basico: true, premium: true, personalizado: true },
    { feature: "Kit de Governança do Canal", basico: true, premium: true, personalizado: true },
    { feature: "Kit de Comunicação Institucional", basico: true, premium: true, personalizado: true },
    { feature: "Dashboard", basico: "Simplificado", premium: "Avançados", personalizado: "Avançados" },
    { feature: "Acessos ao Painel", basico: "1", premium: "3", personalizado: "Conforme projeto" },
    { feature: "Suporte Técnico Funcional da Plataforma", basico: "E-mail", premium: "E-mail e Chat", personalizado: "Personalizado" },
    { feature: "Prazo do Suporte Técnico", basico: "Até 48h úteis", premium: "Até 24h úteis", personalizado: "Conforme SLA" },
    { feature: "SLA", basico: "Padrão", premium: "Prioritário", personalizado: "Personalizado" },
    { feature: "Customizações", basico: "–", premium: "–", personalizado: "Conforme projeto" },
    { feature: "Mensalidade", basico: "R$ 499,99", premium: "R$ 899,99", personalizado: "Sob consulta" }
  ];

  return (
    <>
      <Helmet>
        <title>Planos e Preços - Portal Ethics</title>
        <meta name="description" content="Escolha o plano ideal para fortalecer a cultura ética da sua organização." />
      </Helmet>

      <div className="min-h-screen bg-background-light">
        <Header />
        
        <main className="pt-32 pb-20">
          <div className="max-w-7xl mx-auto px-6">
            {/* Hero */}
            <div className="max-w-[1200px] w-full flex flex-col items-center text-center gap-6 mb-16 mx-auto">
              <h1 className="text-neutral-900 text-5xl font-black leading-tight tracking-tight">
                Planos e Preços
              </h1>
              <p className="text-neutral-600 text-lg max-w-2xl">
                Transparência e conformidade para todos os tamanhos de empresa. Escolha o plano ideal para fortalecer a cultura ética da sua organização.
              </p>
              
              {/* Toggle Billing */}
              <div className="flex items-center gap-4 bg-neutral-200/50 p-1.5 rounded-xl mt-4">
                <button 
                  onClick={() => setBillingCycle('monthly')}
                  className={`px-6 py-2 rounded-lg text-sm font-bold transition-all ${
                    billingCycle === 'monthly' 
                      ? 'bg-white shadow-sm text-primary-blue' 
                      : 'text-neutral-500 hover:text-neutral-700'
                  }`}
                >
                  Mensal
                </button>
                <button 
                  onClick={() => setBillingCycle('yearly')}
                  className={`px-6 py-2 rounded-lg text-sm font-bold transition-all flex gap-2 items-center ${
                    billingCycle === 'yearly' 
                      ? 'bg-white shadow-sm text-primary-blue' 
                      : 'text-neutral-500 hover:text-neutral-700'
                  }`}
                >
                  Anual 
                  {hasYearlyDiscount && (
                    <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full">-20%</span>
                  )}
                </button>
              </div>
            </div>

            {/* Pricing Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-[1140px] w-full px-4 mb-24 mx-auto">
              {plans.map((plan, index) => (
                <div 
                  key={index}
                  className={`relative flex flex-col gap-8 rounded-2xl border p-8 transition-shadow hover:shadow-md ${
                    plan.isPopular 
                      ? "border-2 border-primary-blue bg-white shadow-xl transform scale-105 z-10" 
                      : "border-neutral-200 bg-white shadow-sm"
                  }`}
                >
                  {plan.isPopular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary-blue text-white text-[10px] font-black uppercase tracking-widest px-4 py-1.5 rounded-full">
                      Mais Popular
                    </div>
                  )}
                  
                  <div className="flex flex-col gap-2">
                    <span className={`text-sm font-bold uppercase tracking-widest ${plan.isPopular ? "text-primary-blue" : "text-neutral-500"}`}>
                      {plan.category}
                    </span>
                    <h2 className="text-neutral-900 text-2xl font-bold">{plan.name}</h2>
                    <div className="flex items-baseline gap-1 mt-4">
                      <span className="text-neutral-900 text-4xl font-black tracking-tight">
                        {plan.monthlyPrice === 0 ? "A partir de (Sob Consulta)" : formatPrice(billingCycle === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice)}
                      </span>
                      {plan.monthlyPrice > 0 && <span className="text-neutral-500 text-base">/{billingCycle === 'monthly' ? 'mês' : 'ano'}</span>}
                    </div>
                    {billingCycle === 'yearly' && plan.monthlyPrice > 0 && plan.yearlyDiscountEnabled && (
                      <p className="text-sm text-success mt-1">
                        Economia de {formatPrice(plan.monthlyPrice * 12 - plan.yearlyPrice)}
                      </p>
                    )}
                    <p className="text-neutral-600 text-sm mt-1">{plan.employeeCount}</p>
                    <p className="text-neutral-600 text-sm mt-2">{plan.description}</p>
                  </div>

                  {plan.name === "Personalizado" ? (
                    <a 
                      href="http://wa.me/5511988890360" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="block"
                    >
                      <Button 
                        variant={plan.buttonVariant} 
                        className="w-full"
                      >
                        Contatar Vendas
                      </Button>
                    </a>
                  ) : (
                    <Link to="/cadastro">
                      <Button 
                        variant={plan.buttonVariant} 
                        className="w-full"
                      >
                        {plan.isPopular ? "Assinar Agora" : "Começar Agora"}
                      </Button>
                    </Link>
                  )}

                  <div className="flex flex-col gap-4">
                    <p className="text-neutral-900 text-sm font-bold">
                      {plan.adminAccess}
                    </p>
                    <p className={`text-sm font-bold uppercase tracking-wider ${plan.isPopular ? "text-primary-blue" : "text-neutral-900"}`}>
                      {plan.sectionTitle}
                    </p>
                    <p className="text-neutral-900 text-sm font-bold">
                      {plan.extraFeatures || "O que está incluído:"}
                    </p>
                    <ul className="space-y-3">
                      {plan.features.map((feature, fIndex) => (
                        <li key={fIndex} className="flex items-center gap-3 text-sm text-neutral-600">
                          <CheckCircle className={`w-5 h-5 ${plan.isPopular ? "text-primary-blue fill-primary-blue/20" : "text-primary-blue"}`} />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>

            {/* Comparison Table */}
            <div className="max-w-[1140px] w-full px-4 mt-8 mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-neutral-900 text-3xl font-bold tracking-tight mb-4">Comparativo Completo</h2>
                <p className="text-neutral-500">Compare detalhadamente todos os recursos disponíveis em cada plano.</p>
              </div>

              {/* Desktop Table */}
              <div className="hidden md:block overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-sm">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-neutral-50">
                      <th className="p-6 text-sm font-bold text-neutral-900 border-b border-neutral-200">Recurso</th>
                      <th className="p-6 text-sm font-bold text-neutral-900 border-b border-neutral-200">Essencial (Básico)</th>
                      <th className="p-6 text-sm font-bold text-primary-blue border-b border-neutral-200">Mais Popular + Profissional (Master)</th>
                      <th className="p-6 text-sm font-bold text-neutral-900 border-b border-neutral-200">Enterprise (Personalizado)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100">
                    {comparisonRows.map((row, index) => (
                      <tr key={index}>
                        <td className="p-6 text-sm font-medium text-neutral-700">{row.feature}</td>
                        <td className="p-6">
                          {typeof row.basico === "boolean" ? (
                            row.basico ? <Check className="w-5 h-5 text-green-500" /> : <span className="text-neutral-300">-</span>
                          ) : (
                            <span className="text-xs text-neutral-500">{row.basico}</span>
                          )}
                        </td>
                        <td className="p-6">
                          {typeof row.premium === "boolean" ? (
                            row.premium ? <Check className="w-5 h-5 text-green-500 font-bold" /> : <span className="text-neutral-300">-</span>
                          ) : (
                            <span className={`text-xs ${row.premium.includes("Avançados") ? "text-primary-blue font-bold" : "text-primary-blue"}`}>{row.premium}</span>
                          )}
                        </td>
                        <td className="p-6">
                          {typeof row.personalizado === "boolean" ? (
                            row.personalizado ? <Check className="w-5 h-5 text-green-500 font-bold" /> : <span className="text-neutral-300">-</span>
                          ) : (
                            <span className="text-xs text-neutral-900 font-bold">{row.personalizado}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden space-y-6">
                {comparisonRows.map((row, index) => (
                  <div key={index} className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden">
                    <div className="bg-neutral-50 p-4 border-b border-neutral-200">
                      <h3 className="text-sm font-bold text-neutral-900">{row.feature}</h3>
                    </div>
                    <div className="divide-y divide-neutral-100">
                      <div className="p-4 flex justify-between items-center">
                        <span className="text-sm font-medium text-neutral-500">Essencial (Básico)</span>
                        <span className="text-sm text-right">
                          {typeof row.basico === "boolean" ? (
                            row.basico ? <Check className="w-5 h-5 text-green-500" /> : <span className="text-neutral-300">-</span>
                          ) : (
                            <span className="text-xs text-neutral-500">{row.basico}</span>
                          )}
                        </span>
                      </div>
                      <div className="p-4 flex justify-between items-center bg-blue-50/50">
                        <span className="text-sm font-bold text-primary-blue">Mais Popular + Profissional (Master)</span>
                        <span className="text-sm text-right">
                          {typeof row.premium === "boolean" ? (
                            row.premium ? <Check className="w-5 h-5 text-green-500" /> : <span className="text-neutral-300">-</span>
                          ) : (
                            <span className={`text-xs ${row.premium.includes("Avançados") ? "text-primary-blue font-bold" : "text-primary-blue"}`}>{row.premium}</span>
                          )}
                        </span>
                      </div>
                      <div className="p-4 flex justify-between items-center">
                        <span className="text-sm font-medium text-neutral-500">Enterprise (Personalizado)</span>
                        <span className="text-sm text-right">
                          {typeof row.personalizado === "boolean" ? (
                            row.personalizado ? <Check className="w-5 h-5 text-green-500" /> : <span className="text-neutral-300">-</span>
                          ) : (
                            <span className="text-xs text-neutral-900 font-bold">{row.personalizado}</span>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Trust Badges */}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}
